import { Controller } from "@hotwired/stimulus"
import { pushUrl } from "../turbo/history"

/**
 TabsController allows to toggle between different tabs. The controlled element
 must contain `nav` with elements pointing to the tabs (`aria-controls`) and
 the tabs themselves (matching `id`).

 The controller cross-links the nav items with the tabs (adding `id` to the nav
 item and `aria-labelledby` to the tab) and adds click->tabs#show to the nav
 items. It toggles currentClass on the parent of the nav items on click. It
 automatically detects the initially active tab based on the URL hash (if used)
 or selects the first tab.

    %div{data: { controller: :tabs,
                 tabs_current_class: 'current'} }
      %nav{data: {tabs_target: 'navigation'} }
        %a{href: '#tab1', aria: {controls: 'tab1'}} Tab 1
        %a{href: '#tab2', aria: {controls: 'tab2'}} Tab 2

      %div{id: 'tab1', data: { tabs_target: 'tab' }} Tab 1 content
      %div{id: 'tab2', data: { tabs_target: 'tab' }} Tab 2 content
 */
export default class extends Controller {
  static classes = ["current"]
  static targets = ["tab", "navigation"]
  static values = { displayed: String }

  connect() {
    super.connect()
    this._crossLinkNavigationItemsAndTabs()
    this._detectActiveTab()
    this.ensureTabsVisibility()
    this.showHandler = this._showHandler.bind(this)
    this.element.addEventListener("dialog:connected", this.showHandler)

    this.hashChangeListener = this._onHashChange.bind(this)
    window.addEventListener("hashchange", this.hashChangeListener)
  }

  disconnect() {
    super.disconnect()
    this.element.removeEventListener("dialog:connected", this.showHandler)
    window.removeEventListener("hashchange", this.hashChangeListener)
  }

  _crossLinkNavigationItemsAndTabs() {
    this.tabTargets.forEach((tab) => {
      const navItem = this.tabsMenuItem(tab)
      navItem.id = `tab-nav-${tab.id}`
      navItem.dataset.action = "click->tabs#show"
      tab.setAttribute("aria-labelledby", navItem.id)
    })
  }

  _onHashChange() {
    this._detectActiveTab()
    this.ensureTabsVisibility()
  }

  _detectActiveTab() {
    const tab = this._tabForCurrentHash()

    if (tab) {
      this.displayedValue = tab.id
      return
    }

    if (this.tabTargets.length === 0) return

    /* The hash belongs to something else on the page - a dialog, an anchor.
       Leave the current tab alone and only fall back to the first one when
       there is nothing displayed yet. */
    if (!window.location.hash || !this.displayedValue) {
      this.displayedValue = this.tabTargets[0].id
    }
  }

  _tabForCurrentHash() {
    const id = window.location.hash.slice(1)
    if (!id) return null

    return this.tabTargets.find((tab) => tab.id === id)
  }

  show(e) {
    // let the browser handle modified clicks, so the tab opens in a new window
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return

    const tab = this.tabTargets.find((tab) => {
      return tab.id === e.currentTarget.attributes["aria-controls"].value
    })
    if (tab) {
      // prevent tab anchor from scrolling, push it to history instead
      e.preventDefault()
      this._show(tab)

      // When switching a tab, only the hash shall change
      if (window.location.hash !== `#${tab.id}`) {
        pushUrl(`${window.location.pathname}${window.location.search}#${tab.id}`)
      }
    }
  }

  _show(tab) {
    this.displayedValue = tab.id
    this.ensureTabsVisibility()
  }

  ensureTabsVisibility() {
    this.tabTargets.forEach((tab) => {
      const tabsMenuItemTab = this.tabsMenuItem(tab)
      if (!tabsMenuItemTab) return
      if (tab.id === this.displayedValue) {
        tab.hidden = false
        tabsMenuItemTab.parentNode.classList.add(...this.currentClasses)
        this._sendTabChangedEvent(tab, tabsMenuItemTab)
      } else {
        tab.hidden = true
        tabsMenuItemTab.parentNode.classList.remove(...this.currentClasses)
      }
    })
  }

  tabsMenuItem(tab) {
    return this.navigationTarget.querySelector(`[aria-controls="${tab.id}"]`)
  }

  /* A dialog connected somewhere below us. Reveal the tab holding it - the
     event target is the dialog's wrapper, not a tab. */
  _showHandler(e) {
    const tab = e.target.closest('[data-tabs-target="tab"]')
    if (tab) this._show(tab)
  }

  _sendTabChangedEvent(tab, tabsMenuItemTab) {
    const event = new CustomEvent("tab:changed", {
      bubbles: true,
      detail: { tab: tab, tabsMenuItemTab: tabsMenuItemTab },
    })
    this.element.dispatchEvent(event)
  }
}
